
import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, CheckCircle, XCircle, Search, EyeOff } from 'lucide-react';
import { useBrokenGamesDetection } from '@/hooks/useBrokenGamesDetection';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';

const BrokenGamesDetector = () => {
  const {
    isScanning,
    progress,
    results,
    scanForBrokenGames,
    hideBrokenGames,
    isHidingBrokenGames
  } = useBrokenGamesDetection();

  const handleHideBrokenGames = () => {
    const brokenGameIds = results.broken.map(game => game.id);
    if (brokenGameIds.length > 0) {
      hideBrokenGames(brokenGameIds);
    }
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Search className="w-5 h-5" />
          Broken Games Detector
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Controls */}
        <div className="flex items-center gap-4">
          <Button
            onClick={scanForBrokenGames}
            disabled={isScanning}
            className="flex items-center gap-2"
          >
            <Search className="w-4 h-4" />
            {isScanning ? 'Scanning...' : 'Scan for Broken Games'}
          </Button>
          
          {results.broken.length > 0 && (
            <Button
              onClick={handleHideBrokenGames}
              disabled={isHidingBrokenGames}
              variant="destructive"
              className="flex items-center gap-2"
            >
              <EyeOff className="w-4 h-4" />
              Hide {results.broken.length} Broken Games
            </Button>
          )}
        </div>

        {/* Progress */}
        {isScanning && (
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Scanning games...</span>
              <span>{results.checked} / {results.total}</span>
            </div>
            <Progress value={progress} className="w-full" />
          </div>
        )}

        {/* Results Summary */}
        {results.total > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-500" />
                  <div>
                    <p className="text-2xl font-bold text-green-600">{results.working.length}</p>
                    <p className="text-sm text-muted-foreground">Working Games</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2">
                  <XCircle className="w-5 h-5 text-red-500" />
                  <div>
                    <p className="text-2xl font-bold text-red-600">{results.broken.length}</p>
                    <p className="text-sm text-muted-foreground">Broken Games</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                  <div>
                    <p className="text-2xl font-bold text-yellow-600">{results.errors.length}</p>
                    <p className="text-sm text-muted-foreground">Errors</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Broken Games List */}
        {results.broken.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <XCircle className="w-5 h-5 text-red-500" />
              Broken Games ({results.broken.length})
            </h3>
            <ScrollArea className="h-[300px] w-full border rounded-md p-4">
              <div className="space-y-3">
                {results.broken.map((game, index) => (
                  <div key={game.id}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium truncate">{game.title}</h4>
                        <p className="text-sm text-muted-foreground truncate">{game.game_url}</p>
                        {game.error && (
                          <Badge variant="destructive" className="mt-1 text-xs">
                            {game.error}
                          </Badge>
                        )}
                      </div>
                      <Badge variant="destructive">Broken</Badge>
                    </div>
                    {index < results.broken.length - 1 && <Separator className="mt-3" />}
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}

        {/* Error Games List */}
        {results.errors.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-500" />
              Games with Errors ({results.errors.length})
            </h3>
            <ScrollArea className="h-[200px] w-full border rounded-md p-4">
              <div className="space-y-3">
                {results.errors.map((game, index) => (
                  <div key={game.id}>
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium truncate">{game.title}</h4>
                        <p className="text-sm text-muted-foreground truncate">{game.game_url}</p>
                        {game.error && (
                          <Badge variant="secondary" className="mt-1 text-xs">
                            {game.error}
                          </Badge>
                        )}
                      </div>
                      <Badge variant="secondary">Error</Badge>
                    </div>
                    {index < results.errors.length - 1 && <Separator className="mt-3" />}
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}

        {/* Help Text */}
        <div className="text-sm text-muted-foreground bg-muted p-4 rounded-lg">
          <p className="mb-2">
            <strong>How it works:</strong> This tool checks each game's URL to detect broken links that would show "game not found" errors.
          </p>
          <ul className="list-disc list-inside space-y-1">
            <li>Working games respond successfully to HTTP requests</li>
            <li>Broken games fail to load or return error responses</li>
            <li>Errors indicate network issues or other problems during scanning</li>
            <li>You can hide broken games to prevent users from seeing them</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};

export default BrokenGamesDetector;
